import { client as axios } from '../../axios/client';
import {
  GET_TRAYS,
  GET_TRAYS_SUCCESS,
  GET_TRAYS_ERROR,
  CREATE_TRAY,
  CREATE_TRAY_SUCCESS,
  CREATE_TRAY_ERROR,
  UPDATE_TRAY,
  UPDATE_TRAY_SUCCESS,
  UPDATE_TRAY_ERROR,
  DELETE_TRAY,
  DELETE_TRAY_SUCCESS,
  DELETE_TRAY_ERROR,
  SET_ACTIVE_TRAY,
  GET_TRAY_QUEUE,
  GET_TRAY_QUEUE_SUCCESS,
  GET_TRAY_QUEUE_ERROR,
  TRANSFER_CONTACT_TO_TRAY,
  TRANSFER_CONTACT_TO_TRAY_SUCCESS,
  TRANSFER_CONTACT_TO_TRAY_ERROR,
  TRAY_QUEUE_SYNC_CONTACT,
} from './traysTypes';

/**
 * Lista las bandejas de un customer.
 * GET /trays/customer/{id_customer} → ContactTrayResponse[] (con available_count).
 */
export const getTrays = (filters = {}) => async (dispatch) => {
  dispatch({ type: GET_TRAYS });
  try {
    if (!filters.id_customer) {
      dispatch({ type: GET_TRAYS_SUCCESS, payload: [] });
      return [];
    }
    const params = {
      skip: filters.skip || 0,
      limit: filters.limit || 100,
    };
    const headers = filters.token ? { Authorization: `Bearer ${filters.token}` } : {};
    const response = await axios.get(`/trays/customer/${filters.id_customer}`, { params, headers });
    const items = Array.isArray(response.data) ? response.data : [];
    dispatch({ type: GET_TRAYS_SUCCESS, payload: items });
    return items;
  } catch (error) {
    dispatch({ type: GET_TRAYS_ERROR, payload: error.message });
    return [];
  }
};

/**
 * Normaliza el payload de create/update: separa el token de los datos y
 * deja solo los campos que la API de trays acepta.
 */
const normalizePayload = (data = {}) => {
  const { token, ...rest } = data;
  const body = {};
  if (rest.name !== undefined) body.name = rest.name.trim();
  if (rest.description !== undefined) body.description = rest.description;
  if (rest.strategy !== undefined) body.strategy = rest.strategy;
  if (rest.max_contacts !== undefined) body.max_contacts = rest.max_contacts;
  if (rest.is_default !== undefined) body.is_default = Boolean(rest.is_default);
  if (rest.bot_enabled !== undefined) body.bot_enabled = Boolean(rest.bot_enabled);
  if (rest.id_customer !== undefined) body.id_customer = String(rest.id_customer);
  return { body, token };
};

/**
 * Crea una bandeja.
 * POST /trays/ (body JSON) → 201 ContactTray.
 */
export const createTray = (trayData) => async (dispatch) => {
  dispatch({ type: CREATE_TRAY });
  try {
    const { body, token } = normalizePayload(trayData);
    if (!body.name) throw new Error('El nombre de la bandeja es obligatorio');
    if (!body.id_customer) throw new Error('El ID del cliente es obligatorio');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axios.post('/trays/', body, { headers });
    dispatch({ type: CREATE_TRAY_SUCCESS, payload: response.data });
    return response.data;
  } catch (error) {
    dispatch({ type: CREATE_TRAY_ERROR, payload: error.message });
    throw error;
  }
};

/**
 * Actualiza parcialmente una bandeja.
 * PATCH /trays/{tray_id} → 200 ContactTray actualizado.
 */
export const updateTray = (trayId, trayData) => async (dispatch) => {
  dispatch({ type: UPDATE_TRAY });
  try {
    const { body, token } = normalizePayload(trayData);
    delete body.id_customer; // PATCH no acepta id_customer
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await axios.patch(`/trays/${trayId}`, body, { headers });
    dispatch({ type: UPDATE_TRAY_SUCCESS, payload: response.data });
    return response.data;
  } catch (error) {
    dispatch({ type: UPDATE_TRAY_ERROR, payload: error.message });
    throw error;
  }
};

/**
 * Elimina una bandeja.
 * DELETE /trays/{tray_id} → 204 No Content (sin body).
 */
export const deleteTray = (trayId, token) => async (dispatch) => {
  dispatch({ type: DELETE_TRAY });
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await axios.delete(`/trays/${trayId}`, { headers });
    dispatch({ type: DELETE_TRAY_SUCCESS, payload: trayId });
    return true;
  } catch (error) {
    dispatch({ type: DELETE_TRAY_ERROR, payload: error.message });
    throw error;
  }
};

/**
 * Selecciona la bandeja activa en el chat (null = sin bandeja).
 * No es un thunk: action creator plano.
 */
export const setActiveTray = (trayId) => ({ type: SET_ACTIVE_TRAY, payload: trayId });

export const trayQueueSyncContact = (contact) => ({ type: TRAY_QUEUE_SYNC_CONTACT, payload: { contact } });

/**
 * Trae la cola de una bandeja (contactos con id_tray = tray_id).
 * GET /trays/{tray_id}/queue?offset&limit&id_customer → { items: Contact[], count, offset, limit }.
 * La cola trae contactos completos (enriquecidos); count = total para el badge.
 */
export const getTrayQueue = ({ trayId, id_customer, offset = 0, limit = 50, token }) => async (dispatch) => {
  if (!trayId) return { items: [], count: 0 };
  dispatch({ type: GET_TRAY_QUEUE });
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = { offset, limit };
    if (id_customer) params.id_customer = id_customer;
    const response = await axios.get(`/trays/${trayId}/queue`, { params, headers });
    const data = response.data || {};
    const items = Array.isArray(data.items) ? data.items : [];
    dispatch({ type: GET_TRAY_QUEUE_SUCCESS, payload: { items, count: data.count ?? items.length } });
    return data;
  } catch (error) {
    dispatch({ type: GET_TRAY_QUEUE_ERROR, payload: error.message });
    return { items: [], count: 0 };
  }
};

/**
 * Transfiere un contacto a una bandeja (agente → cola compartida).
 * POST /trays/{tray_id}/contacts con { contact_id } → 201 | 404.
 * El backend setea id_tray y limpia id_agent (exclusión mutua).
 */
export const transferContactToTray = ({ trayId, contactId, id_customer, token }) => async (dispatch) => {
  if (!trayId || !contactId) return false;
  dispatch({ type: TRANSFER_CONTACT_TO_TRAY });
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await axios.post(`/trays/${trayId}/contacts`, { contact_id: contactId, id_customer }, { headers });
    dispatch({ type: TRANSFER_CONTACT_TO_TRAY_SUCCESS, payload: { trayId, contactId } });
    return true;
  } catch (error) {
    dispatch({ type: TRANSFER_CONTACT_TO_TRAY_ERROR, payload: error.message });
    return false;
  }
};

/**
 * Quita un contacto de una bandeja compartida (vuelve a la cola del agente).
 * El id_tray del cliente puede quedar stale tras mover el contacto entre bandejas,
 * así que se resuelve el autoritativo del servidor (GET /contacts/id/{id}) antes de
 * POST /trays/{tray_id}/unassign con { contact_id, id_customer } → 200 Contact
 * (id_tray = null, id_agent = null) | 404.
 * The endpoint is NOT idempotent: a 404 means the contact is no longer in that
 * tray, so the caller must surface it as a dedicated case, not as a generic error.
 */
export const unassignContactFromTray = ({ contactId, id_customer, token }) => async (dispatch) => {
  if (!contactId) return { ok: false, status: 0 };
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const { data: fresh } = await axios.get(`/contacts/id/${contactId}`, { headers });
    const trayId = fresh?.id_tray;
    if (!trayId) return { ok: false, status: 404 };
    await axios.post(`/trays/${trayId}/unassign`, { contact_id: contactId, id_customer }, { headers });
    return { ok: true, status: 200 };
  } catch (error) {
    return { ok: false, status: error?.response?.status ?? 0 };
  }
};
